import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  inject,
  output,
} from '@angular/core';
import { isReadable } from '@ctrl/tinycolor';
import { Store } from '@ngrx/store';
import { interval, filter, ReplaySubject, takeUntil, takeWhile, map } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import { userInfo } from 'app/modules/scootaverse/scootaverse-badging/scootaverse-badging.component';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import Social from 'entity/Social';
import User from 'entity/User';

import { actions, socialAppSelectors } from '../state/app';

import { DEFAULT_ONBOARDING_STEPS, PathStep } from './steps-description';
import { delayFor } from 'app/core/utilities/async-utils';

const DEFAULT_BACKGROUND_COLOR = '#0f2c6a';
const DEFAULT_DIV_BACKGROUND_COLOR = '#ffffff1a';
const OPTIONAL_DIV_BACKGROUND_COLOR = '#091d50';
const DEFAULT_TIME_SKIP_STEP = 15000;
const DEFAULT_TIME_SKIP_STEP_PATH = 5000;
const TIME_SKIP_MOVEMENT_STEP = 7000;

@Component({
  selector: 'app-onboarding-experience',
  templateUrl: './onboarding-experience.component.html',
  styleUrls: ['./onboarding-experience.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingExperienceComponent extends Loggable implements OnDestroy, OnInit, OnChanges {
  private store = inject(Store);

  private destroyed$ = new ReplaySubject<boolean>(1);

  protected readonly currentStep = signal<number>(1);
  protected readonly currentPathStep = signal<number>(1);
  protected readonly pathStepsLimit = signal<number>(1);
  protected readonly pathSteps = signal<PathStep[]>([]);
  protected readonly showGroupingGif = signal<boolean>(false);
  protected readonly showStageMagicGif = signal<boolean>(false);
  protected readonly toggleMiniMap = signal<boolean>(false);
  public readonly showBadge = signal<boolean>(false);
  private readonly isConnectedRooms = signal<boolean>(false);
  public readonly pathStepProgress = signal<number>(0); // Initialize progress at 0%
  public readonly stepProgress = signal<number>(0); // Initialize progress at 0%
  public readonly userInfo = signal<userInfo | undefined>(undefined);
  private readonly contrastChecker = signal<boolean>(false);
  public readonly showOverlay = signal<boolean>(true);
  public readonly overlayText = signal<string>('Onboarding is Loading');

  protected readonly themeBackgroundColor = computed(
    () => this.social()?.theme?.colorBackground ?? DEFAULT_BACKGROUND_COLOR,
  );
  protected readonly backgroundDivColor = computed(() =>
    isReadable(DEFAULT_DIV_BACKGROUND_COLOR, this.themeBackgroundColor())
      ? DEFAULT_DIV_BACKGROUND_COLOR
      : OPTIONAL_DIV_BACKGROUND_COLOR,
  );
  protected readonly stepDescription = computed(
    () => this.pathSteps().find((step) => step.id === this.currentPathStep())?.message ?? '',
  );
  protected readonly stepText = computed(() => `Step ${this.stepNumber()} of ${this.numberOfSteps()}`);
  protected readonly numberOfSteps = computed(() => (this.showSmartBadging() ? '4' : '3'));
  protected readonly stepNumber = computed(() =>
    !this.showSmartBadging() && this.currentStep() === 4 ? '3' : this.currentStep().toString(),
  );
  protected readonly mockVideoStep = computed(() => `${this.currentStep()}-${this.currentPathStep()}`);

  public readonly user = input<User | undefined>();
  public readonly social = input<Social | undefined>();
  public readonly showSmartBadging = input<boolean>(false);
  public readonly shouldContinueFromSmartBadging = input<boolean>(false);

  public readonly finishOnboardingExperience = output<boolean>();

  public constructor() {
    super();
    this.setDisplayName('OnboardingExperienceComponent');
  }

  // eslint-disable-next-line @angular-eslint/no-async-lifecycle-method
  public async ngOnInit(): Promise<void> {
    this.contrastChecker.set(isReadable(DEFAULT_DIV_BACKGROUND_COLOR, this.themeBackgroundColor()));

    this.store
      .select(coreSelectors.getUserAndProfileAndAccountInfo)
      .pipe(
        map(({ company, user, userProfile }) => ({
          companyName: company?.companyName,
          title: userProfile?.title,
          aboutMe: userProfile?.description,
          linkedIn: userProfile?.linkedIn,
          department: userProfile?.department,
          officeLocation: userProfile?.officeLocation,
          firstName: user.user?.firstName,
          lastName: user.user?.lastName,
        })),
        takeUntil(this.destroyed$),
      )
      .subscribe((info) => {
        this.userInfo.set(info);
      });

    this.store
      .select(socialAppSelectors.getCurrentConnectedRoomSet)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((set) => {
        this.isConnectedRooms.set(set.socials ? set.socials.length > 1 : false);
      });

    this.updatePathSteps();

    this.store
      .select(socialAppSelectors.hasOnboardingVideoEnded)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((hasOnboardingVideoEnded) => {
        if (hasOnboardingVideoEnded) {
          if (this.currentPathStep() === this.pathStepsLimit()) {
            this.stepLoadingBar();
          } else {
            this.pathStepLoadingBar();
          }
        }
      });

    await this.hideOverlay();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.shouldContinueFromSmartBadging && changes.shouldContinueFromSmartBadging.currentValue) {
      this.currentStep.set(3);
      this.showBadge.set(true);
      this.skipStep();
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public skipStep(buttonClicked?: boolean): void {
    if (buttonClicked) {
      this.stepProgress.set(-1);
    }

    if (this.currentStep() === 4) {
      this.finishOnboardingExperience.emit(true);
    } else if (this.currentStep() === 2) {
      this.handleToggleMiniMap(false);
    } else if (this.currentStep() === 3 && buttonClicked) {
      this.updateOnboardingVideoState(false);
      this.finishOnboardingExperience.emit(false);
    }

    // Skip step related to smart badging
    if (this.currentStep() === 2 && !this.showSmartBadging()) {
      this.currentStep.set(this.currentStep() + 1);
    }

    this.currentStep.set(this.currentStep() + 1);

    this.updatePathSteps();
  }

  public nextStep(buttonClicked?: boolean): void {
    if (buttonClicked) {
      this.pathStepProgress.set(-1);
    }

    if (this.currentPathStep() === this.pathStepsLimit()) {
      if (this.currentStep() === 1) {
        this.showGroupingGif.set(false);
      }

      this.skipStep();
      return;
    }

    if (this.currentStep() === 2 && !this.isConnectedRooms() && this.currentPathStep() === 2) {
      this.skipStep();
      return;
    }

    if (this.currentStep() === 3 && this.currentPathStep() === 1) {
      this.openOnboardingMockBadge(true);
    }

    if (this.currentStep() === 4) {
      this.currentPathStep.set(3);
    } else {
      this.currentPathStep.set(this.currentPathStep() + 1);
    }

    // Verification to open grouping gif when is needed
    if (this.currentStep() === 1) {
      this.showGroupingGif.set(this.currentPathStep() === 5 || this.currentPathStep() === 6);
      return;
    }

    if (this.currentStep() === 2 && this.currentPathStep() === 2) {
      this.handleToggleMiniMap(true);
      return;
    }

    // Verification to return to smart badging questions
    if (this.currentStep() === 3 && this.currentPathStep() === 4) {
      this.openOnboardingMockBadge(false);
      this.showBadge.set(false);
      this.updateOnboardingVideoState(false);
      this.finishOnboardingExperience.emit(false);
      return;
    }

    if (this.currentStep() === 4 && this.currentPathStep() === 3) {
      this.showBadge.set(false); // Ensure this line exists and is correct
      this.showStageMagicGif.set(true);
      return;
    }
  }

  private handleToggleMiniMap(shouldOpen: boolean): void {
    this.store.dispatch(actions.TOGGLE_OPEN_MAP_ONBOARDING_EXPERIENCE({ openMapOnboardingExperience: shouldOpen }));
    this.toggleMiniMap.set(shouldOpen);
  }

  private updatePathSteps(): void {
    this.pathSteps.set(DEFAULT_ONBOARDING_STEPS.find((step) => step.stepId === this.currentStep())?.pathStep ?? []);
    this.pathStepsLimit.set(this.pathSteps().length);

    if (this.currentStep() === 4) {
      // Different first step if smart badging is enabled
      this.currentPathStep.set(this.showSmartBadging() ? 1 : 2);
    } else {
      this.currentPathStep.set(1);
    }
  }

  private pathStepLoadingBar(): void {
    const duration = this.isMovementStep ? TIME_SKIP_MOVEMENT_STEP : DEFAULT_TIME_SKIP_STEP_PATH; // Total duration in milliseconds
    const intervalTime = 100; // Interval between updates in milliseconds
    const increment = 100 / (duration / intervalTime); // Increment per interval
    this.pathStepProgress.set(0);

    interval(intervalTime)
      .pipe(
        takeWhile(() => this.pathStepProgress() < 100 && this.pathStepProgress() >= 0),
        takeUntil(this.destroyed$),
      ) // Stop when progress reaches 100%
      .subscribe({
        next: () => {
          this.pathStepProgress.set(Math.min(this.pathStepProgress() + increment, 100));
          if (this.pathStepProgress() === 100) {
            this.nextStep();
          }
        },
        complete: () => {
          this.pathStepProgress.set(0);
        },
      });
  }

  private stepLoadingBar(): void {
    const duration = DEFAULT_TIME_SKIP_STEP; // Total duration in milliseconds
    const intervalTime = 100; // Interval between updates in milliseconds
    const increment = 100 / (duration / intervalTime); // Increment per interval
    this.stepProgress.set(0);

    interval(intervalTime)
      .pipe(
        takeWhile(() => this.stepProgress() < 100 && this.stepProgress() >= 0),
        takeUntil(this.destroyed$),
      ) // Stop when progress reaches 100%
      .subscribe({
        next: () => {
          this.stepProgress.set(Math.min(this.stepProgress() + increment, 100));
          if (this.stepProgress() === 100) {
            this.nextStep();
          }
        },
        complete: () => {
          this.stepProgress.set(0);
        },
      });
  }

  public formatUserName(firstName?: string, lastName?: string): string {
    return lastName ? `${firstName} ${lastName}` : `${firstName}`;
  }

  private async hideOverlay(): Promise<void> {
    await delayFor(2500);
    this.showOverlay.set(false);
  }

  private get isMovementStep(): boolean {
    return this.currentPathStep() === 3 && this.currentStep() === 1;
  }

  public openOnboardingMockBadge(shouldOpen: boolean): void {
    this.store.dispatch(actions.TOGGLE_ONBOARDING_MOCK_BADGE({ openOnboardingMockBadge: shouldOpen }));
  }

  private updateOnboardingVideoState(hasEnded: boolean): void {
    this.store.dispatch(actions.UPDATE_ONBOARDING_VIDEO_STATE({ onboardingVideoEnded: hasEnded }));
  }
}
